import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  PLATFORM_ENVIRONMENT,
  TERMS_OR_PRIVACY_POLICY_UPDATE_NOTIFICATION_PRIORIZATION_LEVEL,
  YATTA_PRIVACY_POLICY_URL,
  YATTA_TERMS_OF_SERVICE_URL
} from '@platform-frontend/platform';
import { NotificationOptions, ShowInfoNotification } from '@platform-frontend/util';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { MSG_PLATFORM_PRIVACY_UPDATE, MSG_PLATFORM_TERMS_AND_PRIVACY_UPDATE, MSG_PLATFORM_TERMS_UPDATE } from '../notification-messages';
import { SessionSet } from '../session';
import { InitStartup } from '../startup.actions';
import { TermsType, TermsUpdate } from './model/terms-update';
import { UpdatedTermsStateModel } from './model/updated-terms-state-model';
import { TermsUpdateService } from './terms-update.service';
import { ConfirmUpdatedTerms, ShowUpdatedTermsNotification } from './updated-terms.actions';

const DEFAULT_STATE: UpdatedTermsStateModel = {
  termsUpdates: [],
  termsUpdatesInitialized: false
};

@State<UpdatedTermsStateModel>({
  name: 'updatedTerms',
  defaults: DEFAULT_STATE
})
@Injectable()
export class UpdatedTermsState {
  constructor(private http: HttpClient, private termsUpdateService: TermsUpdateService) {}

  @Selector()
  static platformTermsUpdates(state: UpdatedTermsStateModel): TermsUpdate[] {
    return state.termsUpdates;
  }

  @Selector([UpdatedTermsState.platformTermsUpdates])
  static platformTermsUpdatesTypes(state: UpdatedTermsStateModel, termsUpdates: TermsUpdate[]): TermsType[] {
    return termsUpdates.map(update => update.termsType);
  }

  @Selector([UpdatedTermsState.platformTermsUpdates])
  static arePlatformTermsUpdated(state: UpdatedTermsStateModel, termsUpdates: TermsUpdate[]): boolean {
    return termsUpdates.length > 0;
  }

  @Action(SessionSet)
  sessionSet(ctx: StateContext<UpdatedTermsStateModel>, { isSignedIn }: SessionSet): Observable<unknown> {
    return this.checkForUpdatedTerms(ctx, isSignedIn);
  }

  @Action(InitStartup)
  InitStartup(ctx: StateContext<UpdatedTermsStateModel>, { isSignedIn }: InitStartup): Observable<unknown> {
    return ctx.getState().termsUpdatesInitialized ? of(null) : this.checkForUpdatedTerms(ctx, isSignedIn);
  }

  private checkForUpdatedTerms(ctx: StateContext<UpdatedTermsStateModel>, isSignedIn: boolean): Observable<unknown> {
    ctx.patchState({ termsUpdatesInitialized: true });

    if (!isSignedIn) {
      ctx.patchState({ termsUpdates: [] });
      return of(null);
    }

    return this.http.get<TermsUpdate[]>(PLATFORM_ENVIRONMENT.apiUrl + '/platform-terms-updates').pipe(
      catchError(() => {
        ctx.patchState({ termsUpdatesInitialized: false });
        return of([]);
      }),
      tap(termsUpdates => ctx.patchState({ termsUpdates }))
    );
  }

  @Action(ShowUpdatedTermsNotification)
  showUpdatedTermsNotification(ctx: StateContext<UpdatedTermsStateModel>): Observable<unknown> {
    const termsUpdatesTypes = UpdatedTermsState.platformTermsUpdatesTypes(ctx.getState(), ctx.getState().termsUpdates);
    const termsUpdate = termsUpdatesTypes.includes('TERMS_AND_CONDITIONS');
    const privacyUpdate = termsUpdatesTypes.includes('PRIVACY_STATEMENT');
    const termsUpdateNotificationOptions: Partial<NotificationOptions> = {
      autoDismiss: true,
      onDismiss: () => this.confirmPlatformTermsUpdates(ctx, termsUpdatesTypes),
      priorizationLevel: TERMS_OR_PRIVACY_POLICY_UPDATE_NOTIFICATION_PRIORIZATION_LEVEL
    };
    if (termsUpdate && privacyUpdate) {
      return ctx.dispatch(
        new ShowInfoNotification(MSG_PLATFORM_TERMS_AND_PRIVACY_UPDATE(YATTA_TERMS_OF_SERVICE_URL, YATTA_PRIVACY_POLICY_URL), termsUpdateNotificationOptions)
      );
    } else if (termsUpdate) {
      return ctx.dispatch(new ShowInfoNotification(MSG_PLATFORM_TERMS_UPDATE(YATTA_TERMS_OF_SERVICE_URL), termsUpdateNotificationOptions));
    } else if (privacyUpdate) {
      return ctx.dispatch(new ShowInfoNotification(MSG_PLATFORM_PRIVACY_UPDATE(YATTA_PRIVACY_POLICY_URL), termsUpdateNotificationOptions));
    } else {
      return of(null);
    }
  }

  @Action(ConfirmUpdatedTerms)
  confirmUpdatedTerms(ctx: StateContext<UpdatedTermsStateModel>, { types }: ConfirmUpdatedTerms): Observable<unknown> {
    return this.confirmPlatformTermsUpdates(ctx, types);
  }

  private confirmPlatformTermsUpdates(ctx: StateContext<UpdatedTermsStateModel>, types: TermsType[]): Observable<unknown> {
    ctx.patchState({ termsUpdates: [] });
    return this.termsUpdateService.confirmPlatformTermsUpdate(types);
  }
}
